import dayjs from 'dayjs';
import { API } from '../Client';
import { OldAPI } from '../OldClient';

import { DailyAndWeeklyPortfolioHistory, PortfolioHistory, PortfolioPeriodType, Position, TradingAccount } from './types';
import AssetService from '../Asset';
import { PolygonQuote } from '@/hooks/usePositions';

namespace TradingAccountService {
  /**
   * Returns user's trading account
   *
   * @returns
   */
  export async function getTradingAccount(): Promise<TradingAccount> {
    const res = await API.get('/trading/account');
    return res?.data;
  }

  /**
   * Gets user's current positions
   *
   * @returns
   */
  export async function getPositions(includeMetadata = false): Promise<Position[]> {
    console.log('getPositions')
    const res = await API.get('/trading/positions', {
      params: {
        includeMetadata,
      }
    });
    const positions = res?.data || [];

    const symbols = positions?.map((x: any) => x.symbol);

    console.log('symbols::', symbols);
    try {
      const result = await AssetService.getBatchQuote(symbols, false);
      console.log('quotes::', result);
      const updatedPositions = positions?.map((pos: any) => {
        const positionData = result[pos.symbol] as PolygonQuote;
        if (!positionData) {
          return pos;
        }
  
        const updatedLastPrice: number = positionData?.realLatestPrice || parseFloat(pos?.lastday_price) || 0;
        const prevClose: number | undefined = positionData?.previousClose;
  
        const qty: number = Number(pos.qty);
        const qtyBeforeToday: number = Number(pos.qty_traded_before);
        const qtyToday: number = Number(pos.qty_traded_today);
        const entryPriceToday: number = Number(parseFloat(pos.avg_entry_price_today) || 0);
  
        const todayAbsoluteGain =
          (updatedLastPrice - prevClose) * qtyBeforeToday +
          (qtyToday ? (updatedLastPrice - entryPriceToday) * qtyToday : 0);
        const todayPercentGain =
          (((updatedLastPrice - prevClose) * 100 * qtyBeforeToday) / prevClose +
            (qtyToday
              ? ((updatedLastPrice - entryPriceToday) * 100 * qtyToday) / entryPriceToday
              : 0)) /
          qty;
  
        return {
          ...pos,
          current_price: updatedLastPrice,
          lastday_price: updatedLastPrice,
          unrealized_intraday_pl: todayAbsoluteGain,
          unrealized_intraday_plpc: todayPercentGain / 100,
        };
      });
      return updatedPositions;
    } catch (error) {
      console.error('Error fetching batch quote:', error);
      return []; // return an empty array or handle the error as needed
    }
  }

  /**
   * Gets a single position from user's positions
   *
   * @param symbol
   * @returns
   */
  export async function getPosition(symbol: string): Promise<Position | undefined> {
    try {
      const res = await API.get(`/trading/positions/${symbol}`);
      return res?.data;
    } catch (e: any) {
      if (e?.response?.data?.code === 40410000) {
        return undefined;
      }
      console.error(e?.response?.data || e);
    }
  }

  /**
   * Gets portfolio history for the portfolio history graph
   *
   * @param alpacaId
   * @param period
   * @param accountCreatedAt unix timestamp of account created at
   * @returns
   */
  export async function getPortfolioHistory(
    alpacaId: string,
    period: PortfolioPeriodType,
    accountCreatedAt: number
  ): Promise<PortfolioHistory | undefined> {
    try {
      if (!alpacaId) {
        // Sentry.captureMessage(
        //     'Alpaca Id is undefined for portfolio history call',
        //     Sentry.Severity.Critical,
        // );
        console.error('Alpaca Id is undefined for portfolio history call');
        return undefined;
      }

      // Truncate period start time if their account was created during the selected time period
      const numMonthsAgo = dayjs().diff(dayjs(accountCreatedAt), 'months') + 1;
      let adjustedPeriod = period as string;

      if (
        (period === PortfolioPeriodType._1M && numMonthsAgo < 1) ||
        (period === PortfolioPeriodType._3M && numMonthsAgo < 3) ||
        (period === PortfolioPeriodType._1A && numMonthsAgo < 12) ||
        (period === PortfolioPeriodType._2A && numMonthsAgo < 24)
      ) {
        adjustedPeriod = `${numMonthsAgo}M`;
      }

      const params = {
        period: adjustedPeriod,
        extended_hours: false,
        timeframe: period === PortfolioPeriodType._1D ? '5Min' : period === PortfolioPeriodType._1W ? '1H' : '1D',
      };
      const payload = await OldAPI.get('/accounts/portfolioHistory', {
        params: { ...params, alpacaId },
      });

      return payload.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  export async function getDailyAndWeeklyPortfolioHistory(alpacaId: string): Promise<DailyAndWeeklyPortfolioHistory | undefined> {
    try {
      if (!alpacaId) {
        console.error('Alpaca Id is undefined for portfolio history call');
        return undefined;
      }
      const payload = await OldAPI.get('/accounts/portfolioHistory/dailyAndWeekly', {
        params: { alpacaId },
      });

      return payload.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }
}

export default TradingAccountService;
